/**
 * "Asset Positions" tab of the MARS portfolio dashboard.
 *
 * Asset-class chooser + period control + scenario toggles, above a single
 * exposures time-series chart. The chart is rebuilt client-side from one
 * `fetchTimeseries` payload whenever a selector changes — see
 * `buildExposuresPlot`; no refetch happens on selector changes. A
 * loaded-but-`success:false` payload is surfaced as an error rather than
 * an empty state.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import PlotlyChart from '@/lib/plotly/PlotlyChart';
import { useAuth } from '@/lib/auth/useAuth';
import { ChartCard } from '@/components/ChartCard';
import QueryState from '@/components/QueryState';
import SegmentedControl from '@/components/SegmentedControl';

import AssetToggles from './AssetToggles';
import ExposuresAssetChooser from './ExposuresAssetChooser';
import ExposuresDownloadButton from './ExposuresDownloadButton';
import { buildExposuresPlot } from '../lib/exposuresChart';
import { fetchTimeseries, marsPortfolioKeys } from '../api';
import { buildAssetPositionsDescription } from '../lib/utils';
import { SectionHeader } from './sectionUi';
import { useMarsPortfolioStore } from '../store';
import type { Fund, Timeframe } from '../types';

interface ExposuresSectionProps {
  fund: Fund;
}

const DEFAULT_ASSETS = ['Equities', 'Fixed Income', 'Commodities', 'Inflation Swaps'];
const TIMEFRAME_OPTIONS: ReadonlyArray<{ value: Timeframe; label: string }> = [
  { value: '0.5y', label: '6M' },
  { value: '1y', label: '1Y' },
  { value: '3y', label: '3Y' },
  { value: '10y', label: '10Y' },
  { value: '20y', label: '20Y' },
];

export default function ExposuresSection({ fund }: ExposuresSectionProps): JSX.Element {
  const selectedAssets = useMarsPortfolioStore((s) => s.selectedAssets);
  const toggleAsset = useMarsPortfolioStore((s) => s.toggleAsset);
  const timeframe = useMarsPortfolioStore((s) => s.timeframe);
  const setTimeframe = useMarsPortfolioStore((s) => s.setTimeframe);
  const volTiming = useMarsPortfolioStore((s) => s.volTiming);
  const constraints = useMarsPortfolioStore((s) => s.constraints);
  const tc = useMarsPortfolioStore((s) => s.tc);
  const { isFulcrumInternal } = useAuth();

  const query = useQuery({
    queryKey: marsPortfolioKeys.timeseries(fund.id),
    queryFn: () => fetchTimeseries(fund.id),
    staleTime: 60_000,
  });

  const plot = useMemo(() => {
    if (!query.data?.success) return { data: [], layout: {} };
    return buildExposuresPlot(fund, query.data.data, selectedAssets, timeframe, {
      volTiming,
      constraints,
      tc,
    });
  }, [query.data, fund, selectedAssets, timeframe, volTiming, constraints, tc]);

  const assetClasses = fund.asset_classes.length > 0 ? fund.asset_classes : DEFAULT_ASSETS;

  return (
    <section>
      <div className="px-4">
        <SectionHeader
          title="Summary Metrics"
          description={buildAssetPositionsDescription(fund.id)}
        />
      </div>

      <div className="px-4">
        <ExposuresAssetChooser
          assetClasses={assetClasses}
          selected={selectedAssets}
          onToggle={toggleAsset}
        />

        {/*
         * Controls row — Period (left), Vol Timing/Transaction Costs toggles
         * (center), Download CSV (right). Keeps ~30px above and ~60px below
         * this row; a tighter my-4 collapses the chart upward by ~50px.
         */}
        <div className="mt-6 mb-14 flex flex-wrap items-center justify-between gap-4">
          <SegmentedControl
            variant="group"
            label="Period:"
            options={TIMEFRAME_OPTIONS}
            value={timeframe}
            onChange={setTimeframe}
          />
          <AssetToggles fund={fund} inline />
          {isFulcrumInternal ? <ExposuresDownloadButton fundId={fund.id} /> : null}
        </div>

        <ChartCard height={500}>
          <QueryState
            query={{
              isLoading: query.isLoading,
              // A loaded-but-unsuccessful payload is treated as an error so it
              // shows the error message rather than the empty one.
              isError: query.isError || (!query.isLoading && !query.data?.success),
              data: plot,
            }}
            isEmpty={(p) => p.data.length === 0}
            loadingLabel="Loading chart…"
            errorLabel="Failed to load exposures"
            emptyLabel="Select assets to view portfolio data"
          >
            {(p) => <PlotlyChart data={p.data} layout={p.layout} />}
          </QueryState>
        </ChartCard>
      </div>
    </section>
  );
}
